import BoxIcon from '@components/Icons/BoxIcon';
import FrameIoIcon from '@components/Icons/FrameIoIcon';
import ShadeColorIcon from '@components/Icons/ShadeColorIcon';
import SoundCloudIcon from '@components/Icons/SoundCloudIcon';
import TikTokColorIcon from '@components/Icons/TikTokColorIcon';
import { getPlatformConfig, PlatformKey } from '@lib/utils/platforms';
import { OpenInNew } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import React, { useState } from 'react';
import css from './ExternalLinkIcon.module.scss';

// Constants
const TOOLTIP_DELAY_MS = 500;

// Platforms with no PNG in @assets/channel-pngs — they render an SVG component
// instead. Anything not listed here (and with no PNG) falls back to OpenInNew.
const SVG_PLATFORM_ICONS: Partial<Record<PlatformKey, React.ReactNode>> = {
  frameio: <FrameIoIcon />,
  box: <BoxIcon />,
  shade: <ShadeColorIcon />,
  tiktok: <TikTokColorIcon />,
  soundcloud: <SoundCloudIcon />,
};

interface ExternalLinkIconProps {
  origin: Media['origin'];
  className?: string;
}

export const ExternalLinkIcon: React.FC<ExternalLinkIconProps> = ({ origin, className }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Check if this is an external link with a URL
  const isExternalLink = origin.source === 'external' && origin.url;

  if (!isExternalLink || origin.source !== 'external' || !origin.url) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(origin.url, '_blank', 'noopener,noreferrer');
  };

  const handleMouseEnter = () => {
    const timeout = setTimeout(() => {
      setShowTooltip(true);
    }, TOOLTIP_DELAY_MS);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setShowTooltip(false);
  };

  // Get platform configuration
  const type = origin.type?.toLowerCase();
  let platformKey: PlatformKey = 'generic';

  // Special cases: backend may store type as 'generic' for platforms it re-resolves via CDN URLs
  if (type === 'generic' && origin.url) {
    const lowerUrl = origin.url.toLowerCase();
    if (lowerUrl.includes('dropbox')) {
      platformKey = 'dropbox';
    } else if (lowerUrl.includes('frame.io')) {
      platformKey = 'frameio';
    } else if (lowerUrl.includes('boxcloud.com')) {
      platformKey = 'box';
    }
  } else if (type) {
    platformKey = type as PlatformKey;
  }

  // getPlatformConfig handles extractor name normalization (e.g. "next.frame.io" → "frameio")
  const platformConfig = getPlatformConfig(platformKey);
  // Use the config's key as the resolved platform key (normalized)
  const resolvedKey = platformConfig.key;

  // Build icon element
  const iconElement = platformConfig.icon ? (
    <img src={platformConfig.icon} alt={platformConfig.displayName} />
  ) : (
    (SVG_PLATFORM_ICONS[resolvedKey] ?? <OpenInNew />)
  );

  // Get CSS class for platform
  const platformCssClass = css[resolvedKey] || '';

  return (
    <Tooltip title={platformConfig.tooltipText || ''} open={showTooltip} placement="top" arrow>
      <span
        className={classNames(css.externalLinkIcon, platformCssClass, className)}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as any);
          }
        }}
      >
        {iconElement}
      </span>
    </Tooltip>
  );
};

export default ExternalLinkIcon;
